import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight";
import { IconError } from "@/assets/icons/geist/IconError";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { Text } from "@/components/podkit/typography/Text";
import type { AgentMessageFragmentAction } from "@/hooks/use-stream-conversation";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useRef, useState, type FC } from "react";

export type EnvironmentOnboardingActionsTitle = {
    // Title to show when the the agent is done
    completed: string;
    // Title to show while the agent is running
    inProgress: string;
};

export const EnvironmentOnboardingActions: FC<{
    isAgentExecutionDone: boolean;
    title: EnvironmentOnboardingActionsTitle;
    agentExecution: PlainAgentExecution;
    actions: AgentMessageFragmentAction[];
}> = ({ isAgentExecutionDone, title, actions }) => {
    return (
        <div className="dark flex flex-col gap-1 overflow-hidden">
            <ActionsHeader isAgentExecutionDone={isAgentExecutionDone} title={title} />
            <ActionList actions={actions} />
        </div>
    );
};

const ActionsHeader: FC<{
    isAgentExecutionDone: boolean;
    title: EnvironmentOnboardingActionsTitle;
}> = ({ isAgentExecutionDone, title }) => {
    return (
        <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-2">
                {isAgentExecutionDone ? (
                    <Text className="truncate text-sm font-bold">{title.completed}</Text>
                ) : (
                    <TextShimmer className="truncate text-sm font-bold" spread={1.5}>
                        {title.inProgress}
                    </TextShimmer>
                )}
            </div>
        </div>
    );
};

const ActionList: FC<{
    actions: AgentMessageFragmentAction[];
}> = ({ actions }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    return (
        <ScrollArea
            orientation="vertical"
            autoScroll={true}
            autoScrollContentRef={contentRef}
            className="flex h-[300px] rounded-lg border border-border-base bg-always-dark"
        >
            <div ref={contentRef} className="flex min-h-fit min-w-0 flex-grow flex-col gap-1 px-2 py-3">
                {actions.map((action) => (
                    <ActionListItem key={`${action.type}-${action.actionId}`} action={action} />
                ))}
            </div>
        </ScrollArea>
    );
};

export const ActionListItem: FC<{
    action: AgentMessageFragmentAction;
}> = ({ action }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isExpandable = (action.state === "completed" || action.state === "failed") && action.resultDescription;
    return (
        <div className={"flex items-start gap-1"}>
            <Collapsible
                open={isExpanded}
                onOpenChange={setIsExpanded}
                disabled={!isExpandable}
                className="flex min-w-0 flex-grow flex-col"
            >
                <CollapsibleTrigger asChild>
                    <Button
                        variant="text"
                        size="md"
                        className="h-5 max-w-full self-start p-0 text-sm font-normal text-always-light disabled:text-always-light"
                        onClick={() => setIsExpanded(!isExpanded)}
                        data-testid={`action-${action.actionId}`}
                    >
                        {action.state === "completed" &&
                            (isExpanded ? (
                                <IconChevronDown size="sm" className="shrink-0 text-always-light" />
                            ) : (
                                <IconChevronRight size="sm" className="shrink-0 text-always-light" />
                            ))}
                        {action.state === "failed" && <IconError size="sm" className="shrink-0 text-always-light" />}
                        {action.state === "running" && (
                            <IconSpinner size="sm" className="shrink-0 animate-spin text-content-orange" />
                        )}
                        {action.intentDescription}
                    </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="ml-5 mt-1 text-sm text-always-light">
                    <ActionResult action={action} />
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};

const ActionResult: FC<{ action: AgentMessageFragmentAction }> = ({ action }) => {
    return (
        <ScrollArea orientation="both" className="flex max-h-[300px] pl-[3px]">
            <pre className="font-mono text-sm text-always-light">
                <code>{action.resultDescription}</code>
            </pre>
        </ScrollArea>
    );
};
